import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { StatusPill } from "@/components/shared/Visual";
import {
  Users2, ChevronRight, Check, ArrowLeft, Save, Sparkles,
  Users, FileText, ShieldCheck, Activity, Plus, X,
} from "lucide-react";

/**
 * ASSIGN OPERATIONAL ROLES — company/admin wizard
 * Step 3 in a Job Details → Select Team → Assign Roles → Review flow.
 * Assigns Operations Manager (Primary) + 4 support roles per booking.
 * Backend: /api/bookings/:id/ops-roles
 */
export default function AssignOpsRoles() {
  const { id } = useParams();
  const nav = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [booking, setBooking] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [pickerKind, setPickerKind] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/ops/role-catalog").then((r) => setCatalog(r.data)).catch(() => {});
    api.get(`/bookings/${id}`).then((r) => setBooking(r.data)).catch(() => {});
    load();
    // load candidate assignees — full names via company/admin picker endpoint
    api.get("/company/pros-picker").then((r) => setCandidates(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [id]);

  const load = () => api.get(`/bookings/${id}/ops-roles`).then((r) => setAssignments(r.data?.assignments || [])).catch(() => {});

  const find = (kind) => assignments.find((a) => a.kind === kind);

  const assign = async (kind, userId) => {
    setBusy(true);
    try {
      await api.post(`/bookings/${id}/ops-roles`, { kind, assignee_user_id: userId });
      toast.success("Role assigned");
      setPickerKind(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const remove = async (kind) => {
    setBusy(true);
    try {
      await api.delete(`/bookings/${id}/ops-roles/${kind}`);
      toast.success("Role removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const iconFor = (key) => ({
    briefing_coordinator: Users,
    sop_controller: FileText,
    compliance_checker: ShieldCheck,
    live_monitor: Activity,
  }[key] || Users);

  const supportAssigned = assignments.filter((a) => a.kind !== "ops_manager").length;
  const opsManager = find("ops_manager");
  const totalAddon = (catalog?.support || []).reduce((sum, r) => sum + (find(r.key) ? r.addon_price : 0), 0);

  return (
    <div className="space-y-6" data-testid="assign-ops-roles-page">
      {/* Wizard header */}
      <div className="page-hero flex items-start justify-between gap-4 flex-wrap">
        <div className="relative z-10">
          <Link to={`/company/bookings/${id}`} className="text-xs text-brand font-outfit font-bold uppercase tracking-widest flex items-center gap-1 mb-3 hover:underline">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to booking
          </Link>
          <div className="eyebrow eyebrow-brand mb-2">Job wizard</div>
          <h1 className="text-3xl font-outfit font-bold txt-hi">{booking?.job_title || booking?.company_name || "Deployment"}</h1>
        </div>
        <div className="relative z-10 flex items-center gap-2">
          <StatusPill kind="managed">CPO Job</StatusPill>
        </div>
      </div>

      {/* 4-step tracker */}
      <div className="card-dark p-4">
        <div className="flex items-center gap-3 overflow-x-auto">
          {["Job Details", "Select Team", "Assign Roles", "Review & Confirm"].map((s, i) => {
            const active = i === 2;
            const done = i < 2;
            return (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-2 ${active ? "" : done ? "opacity-90" : "opacity-40"}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-outfit font-bold ${active ? "bg-brand text-white" : done ? "bg-gold-500/20 text-gold-300" : "bg-white/5 txt-lo"}`}>
                    {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
                  </span>
                  <span className={`text-xs font-outfit font-semibold uppercase tracking-widest ${active ? "txt-hi" : "txt-lo"}`}>{s}</span>
                </div>
                {i < 3 && <div className="flex-1 h-px bg-white/10 min-w-4" />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Roles column */}
        <div className="lg:col-span-2 space-y-4">
          <div className="eyebrow eyebrow-brand">Assign Operational Roles</div>

          {/* Primary Ops Manager */}
          <div className="card-dark p-5 border-l-4 border-l-violet-500" data-testid="role-ops-manager">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-md bg-violet-500/20 text-violet-300 flex items-center justify-center flex-shrink-0">
                  <Users2 className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="eyebrow eyebrow-brand">Operations Manager (Primary)</div>
                  <div className="font-outfit font-bold txt-hi text-base mt-0.5">{opsManager?.assignee_name || "Not assigned"}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {opsManager && <StatusPill kind="active">Assigned</StatusPill>}
                <button onClick={() => setPickerKind("ops_manager")} className="btn-ghost-dark text-xs">
                  {opsManager ? "Change" : "Assign"}
                </button>
              </div>
            </div>
          </div>

          {/* Support roles */}
          <div className="eyebrow txt-lo">Support roles <span className="text-brand">·</span> assign as needed</div>
          {(catalog?.support || []).map((r) => {
            const Icon = iconFor(r.key);
            const rec = find(r.key);
            return (
              <div key={r.key} className="card-dark p-4" data-testid={`role-${r.key}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className={`w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0
                    ${r.tone === "violet" ? "bg-violet-500/20 text-violet-300" :
                      r.tone === "brand" ? "bg-brand/20 text-brand" :
                      r.tone === "emerald" ? "bg-gold-500/20 text-gold-300" :
                      "bg-amber-500/20 text-amber-300"}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-outfit font-bold txt-hi text-sm">{r.label}</div>
                    <div className="text-xs txt-lo font-manrope mt-0.5">{r.sub}</div>
                    {rec && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                          <Users className="w-3 h-3 txt-md" />
                        </div>
                        <span className="text-sm font-outfit font-semibold txt-hi">{rec.assignee_name}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {r.addon_price > 0 && <span className="text-xs txt-lo font-mono">€{r.addon_price.toFixed(0)}</span>}
                    <button onClick={() => setPickerKind(r.key)} className="btn-ghost-dark text-xs">
                      {rec ? "Change" : "Assign"}
                    </button>
                    {rec && <button onClick={() => remove(r.key)} className="text-xs text-rose-400 hover:text-rose-300 font-outfit font-bold uppercase tracking-widest">Remove</button>}
                  </div>
                </div>
              </div>
            );
          })}

          <button className="card-dark p-4 w-full text-left flex items-center gap-3 hover:border-brand/40" disabled>
            <div className="w-9 h-9 rounded-md bg-white/5 flex items-center justify-center"><Plus className="w-4 h-4 txt-lo" /></div>
            <div>
              <div className="font-outfit font-semibold txt-hi text-sm">Add Custom Role (Optional)</div>
              <div className="eyebrow mt-0.5">Coming soon</div>
            </div>
          </button>
        </div>

        {/* Summary sidebar */}
        <div className="space-y-4">
          <div className="card-dark p-5">
            <div className="eyebrow eyebrow-brand mb-3">Job Summary</div>
            <div className="space-y-2 text-sm font-manrope">
              <SumRow k="Date" v={booking?.date || "—"} />
              <SumRow k="Location" v={booking?.location || "—"} />
              <SumRow k="Guards" v={`${booking?.workers || 0}`} />
              <SumRow k="Job ID" v={id?.slice(0, 8)} mono />
            </div>
          </div>
          <div className="card-dark p-5">
            <div className="eyebrow eyebrow-brand mb-3">Role Overview</div>
            <div className="space-y-3">
              <RingRow n={opsManager ? 1 : 0} label="Primary Ops Manager" />
              <RingRow n={supportAssigned} label="Support roles assigned" />
              <RingRow n={(opsManager ? 1 : 0) + supportAssigned} label="Total operational roles" tone="emerald" />
            </div>
          </div>
          <div className="card-dark p-5">
            <div className="eyebrow eyebrow-brand mb-3">Estimated Add-ons</div>
            <div className="space-y-1.5 text-sm font-manrope">
              {(catalog?.support || []).filter((r) => r.addon_price > 0 && find(r.key)).map((r) => (
                <div key={r.key} className="flex items-center justify-between">
                  <span className="txt-md">{r.label.split(" / ")[0]}</span>
                  <span className="font-mono txt-hi">€{r.addon_price.toFixed(2)}</span>
                </div>
              ))}
              <div className="divider-dark pt-2 mt-2 flex items-center justify-between">
                <span className="eyebrow">Total (est.)</span>
                <span className="font-outfit font-bold txt-hi text-lg">€{totalAddon.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to={`/company/bookings/${id}`} className="btn-ghost-dark">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Link>
        <div className="flex items-center gap-3">
          <button className="btn-ghost-dark"><Save className="w-4 h-4 mr-1" /> Save draft</button>
          <button onClick={() => nav(`/company/bookings/${id}`)} className="btn-brand" data-testid="assign-roles-review">
            Review &amp; Confirm <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        </div>
      </div>

      {/* Candidate picker modal */}
      {pickerKind && (
        <PickerModal
          kind={pickerKind}
          candidates={candidates}
          onClose={() => setPickerKind(null)}
          onPick={(u) => assign(pickerKind, u.id)}
          busy={busy}
        />
      )}
    </div>
  );
}

function SumRow({ k, v, mono }) {
  return (
    <div className="flex items-center justify-between">
      <span className="txt-lo">{k}</span>
      <span className={`txt-hi ${mono ? "font-mono text-xs" : "font-outfit font-semibold"}`}>{v}</span>
    </div>
  );
}

function RingRow({ n, label, tone = "brand" }) {
  const bg = tone === "emerald" ? "bg-gold-500/20 text-gold-300 border-gold-500/40" : "bg-brand/15 text-brand border-brand/30";
  return (
    <div className="flex items-center gap-3">
      <div className={`w-9 h-9 rounded-full border ${bg} flex items-center justify-center font-outfit font-bold`}>{n}</div>
      <span className="text-sm txt-md font-manrope">{label}</span>
    </div>
  );
}

function PickerModal({ kind, candidates, onClose, onPick, busy }) {
  const [q, setQ] = useState("");
  const filtered = candidates.filter((c) => !q || (c.full_name || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div className="card-dark w-full max-w-lg p-5 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="eyebrow eyebrow-brand">Assign {kind.replace(/_/g, " ")}</div>
          <button onClick={onClose} className="txt-lo hover:txt-hi"><X className="w-4 h-4" /></button>
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search verified pros…"
          className="w-full mb-3 rounded-md text-sm p-2.5"
        />
        <div className="space-y-1 max-h-96 overflow-y-auto">
          {filtered.length === 0 && <div className="txt-lo text-sm font-manrope text-center py-6">No pros found.</div>}
          {filtered.map((c) => (
            <button key={c.id} disabled={busy} onClick={() => onPick(c)} className="w-full text-left card-inner p-3 hover:border-brand/40 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-brand/15 text-brand flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-outfit font-semibold txt-hi text-sm truncate">
                  {c.full_name || "—"}
                  <span className="ml-2 text-xs font-mono txt-lo">{c.secusource_id}</span>
                </div>
                <div className="eyebrow mt-0.5">
                  {(c.primary_role || (c.role_categories && c.role_categories[0]) || "Professional")}
                  {c.rating ? ` · ${Number(c.rating).toFixed(1)}★` : ""}
                  {c.cpo_vetting_status === "green" ? " · Verified" : c.cpo_vetting_status === "amber" ? " · Amber" : ""}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 txt-lo" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
